import type { users } from '@techgitter/contracts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CACHE_KEY = 'users_cache_v1';
const TTL_MS = 86_400_000; // 24 hours
const SCHEMA_VERSION = 1;
const API_URL = '/api/users';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CacheEnvelope {
  cachedAt: number;
  ttlMs: number;
  schemaVersion: number;
  data: users[];
}

export interface GetUsersOptions {
  forceRefresh?: boolean;
  allowStale?: boolean;
}

export interface GetUsersResult {
  data: users[];
  isStale?: boolean;
  warning?: string;
}

// ---------------------------------------------------------------------------
// Module-level state (in-memory tier)
// ---------------------------------------------------------------------------

let memoryCache: CacheEnvelope | null = null;
let inflightRequest: Promise<GetUsersResult> | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isFresh(envelope: CacheEnvelope): boolean {
  return Date.now() - envelope.cachedAt < envelope.ttlMs;
}

function readLocalStorage(): CacheEnvelope | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CacheEnvelope = JSON.parse(raw);
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (!parsed.data || !parsed.cachedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalStorage(envelope: CacheEnvelope): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage full or unavailable
  }
}

function buildEnvelope(data: users[]): CacheEnvelope {
  return {
    cachedAt: Date.now(),
    ttlMs: TTL_MS,
    schemaVersion: SCHEMA_VERSION,
    data,
  };
}

function storeEnvelope(envelope: CacheEnvelope): void {
  memoryCache = envelope;
  writeLocalStorage(envelope);
}

function resolveCache(): CacheEnvelope | null {
  if (memoryCache) return memoryCache;
  const stored = readLocalStorage();
  if (stored) {
    memoryCache = stored;
  }
  return stored;
}

async function fetchFromNetwork(): Promise<users[]> {
  const response = await fetch(API_URL);
  if (!response.ok) {
    throw new Error(`Users fetch failed: ${response.status}`);
  }
  const data = await response.json();
  const items = Array.isArray(data) ? data : (data.items ?? []);
  return items as users[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function getUsers(
  options?: GetUsersOptions,
): Promise<GetUsersResult> {
  const { forceRefresh = false, allowStale = true } = options ?? {};

  if (!forceRefresh) {
    const cached = resolveCache();
    if (cached && isFresh(cached)) {
      return { data: cached.data };
    }
  }

  if (inflightRequest && !forceRefresh) {
    return inflightRequest;
  }

  const networkPromise = (async (): Promise<GetUsersResult> => {
    try {
      const data = await fetchFromNetwork();
      const envelope = buildEnvelope(data);
      storeEnvelope(envelope);
      return { data };
    } catch (err: any) {
      if (allowStale) {
        const stale = resolveCache();
        if (stale) {
          return {
            data: stale.data,
            isStale: true,
            warning: `Using cached users (network error: ${err.message})`,
          };
        }
      }
      throw err;
    } finally {
      inflightRequest = null;
    }
  })();

  inflightRequest = networkPromise;
  return networkPromise;
}

export async function invalidateUsersCache(): Promise<void> {
  memoryCache = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Cache metadata
// ---------------------------------------------------------------------------

export function getUsersCacheMetadata(): {
  cachedAt: number;
  ttlMs: number;
  recordCount: number;
} | null {
  const envelope = resolveCache();
  if (!envelope) return null;
  return {
    cachedAt: envelope.cachedAt,
    ttlMs: envelope.ttlMs,
    recordCount: envelope.data.length,
  };
}
